import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";

import {
  AGE_GROUPS,
  BIGGEST_LOSSES,
  BROKERS,
  CAPITAL_OPTIONS,
  CHALLENGES,
  DEFAULT_DRAFT,
  EXPERIENCE_OPTIONS,
  GOALS,
  MARKETS,
  MONTHLY_RESULTS,
  STORAGE_KEY,
  STYLES,
  TRADE_REASONS,
  WIN_RATES,
} from "@/data/assessment";
import { MultiOptionGrid, OptionGrid } from "@/components/OptionGrid";
import LikertGroup from "@/components/LikertGroup";
import { api } from "@/lib/api";

const STEPS = [
  { key: "basics", title: "Basic Details", subtitle: "Let's start with who you are." },
  { key: "profile", title: "Trading Profile", subtitle: "Tell us how you trade today." },
  { key: "performance", title: "Performance", subtitle: "How are the numbers actually looking?" },
  { key: "psychology", title: "Psychology", subtitle: "Answer honestly. There are no wrong answers." },
  { key: "goals", title: "Goals", subtitle: "What are you actually trying to build?" },
];

function loadDraft() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return DEFAULT_DRAFT;
    const parsed = JSON.parse(raw);
    return {
      step: typeof parsed.step === "number" ? parsed.step : 0,
      data: { ...DEFAULT_DRAFT.data, ...(parsed.data || {}) },
    };
  } catch {
    return DEFAULT_DRAFT;
  }
}

export default function Wizard() {
  const navigate = useNavigate();
  const [{ step, data }, setState] = useState(loadDraft);
  const [submitting, setSubmitting] = useState(false);

  // Autosave
  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ step, data }));
  }, [step, data]);

  // Prevent accidental refresh
  useEffect(() => {
    const handler = (e) => {
      // Only warn if user has entered something meaningful
      if (data.name || data.phone) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [data.name, data.phone]);

  const setData = (patch) => setState((s) => ({ ...s, data: { ...s.data, ...patch } }));
  const setStep = (n) => setState((s) => ({ ...s, step: n }));

  const canNext = useMemo(() => validateStep(step, data), [step, data]);

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/assessment", data);
      window.localStorage.removeItem(STORAGE_KEY);
      navigate(`/result/${res.data.id}`);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please fill all required fields.");
    } finally {
      setSubmitting(false);
    }
  };

  const progress = ((step + 1) / STEPS.length) * 100;
  const current = STEPS[step];

  return (
    <div className="mx-auto max-w-2xl px-4 sm:px-6 py-10 sm:py-14">
      {/* Progress */}
      <div className="mb-8">
        <div className="flex items-center justify-between text-xs font-medium text-muted-foreground">
          <span data-testid="wizard-step-indicator">
            Step {step + 1} of {STEPS.length}
          </span>
          <span className="tabular-nums">{Math.round(progress)}%</span>
        </div>
        <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
          <motion.div
            className="h-full bg-primary"
            initial={false}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={current.key}
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          exit={{ x: -20, opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
            {current.key}
          </p>
          <h1
            className="font-display mt-1 text-3xl sm:text-4xl font-semibold tracking-tight"
            data-testid={`step-title-${current.key}`}
          >
            {current.title}
          </h1>
          <p className="mt-2 text-muted-foreground">{current.subtitle}</p>

          <div className="mt-8 space-y-8">
            {step === 0 && <StepBasics data={data} setData={setData} />}
            {step === 1 && <StepProfile data={data} setData={setData} />}
            {step === 2 && <StepPerformance data={data} setData={setData} />}
            {step === 3 && (
              <LikertGroup
                psychology={data.psychology}
                onChange={(psy) => setData({ psychology: psy })}
              />
            )}
            {step === 4 && <StepGoals data={data} setData={setData} />}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Nav */}
      <div className="mt-10 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          data-testid="wizard-prev-btn"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-border text-sm font-medium hover:bg-accent transition disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <ArrowLeft className="w-4 h-4" />
          Previous
        </button>

        {step < STEPS.length - 1 ? (
          <button
            type="button"
            onClick={() => setStep(step + 1)}
            disabled={!canNext}
            data-testid="wizard-next-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:-translate-y-[1px] transition disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none"
          >
            Continue
            <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            type="button"
            onClick={submit}
            disabled={!canNext || submitting}
            data-testid="wizard-submit-btn"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:-translate-y-[1px] transition disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none"
          >
            {submitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generating your report…
              </>
            ) : (
              <>
                Generate my report
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
}

/* -------- Individual steps -------- */

function Field({ label, required, children, hint }) {
  return (
    <div>
      <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
        {label}
        {required && <span className="text-primary ml-1">*</span>}
      </label>
      <div className="mt-2">{children}</div>
      {hint && <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function TextInput({ value, onChange, placeholder, testId, type = "text" }) {
  return (
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      type={type}
      data-testid={testId}
      className="w-full px-4 py-3 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition"
    />
  );
}

function StepBasics({ data, setData }) {
  return (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full Name" required>
          <TextInput
            testId="input-name"
            value={data.name}
            onChange={(v) => setData({ name: v })}
            placeholder="Rahul Sharma"
          />
        </Field>
        <Field label="Phone Number" required>
          <TextInput
            testId="input-phone"
            value={data.phone}
            onChange={(v) => setData({ phone: v.replace(/[^0-9+ ]/g, "") })}
            placeholder="98765 43210"
          />
        </Field>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Email">
          <TextInput
            testId="input-email"
            value={data.email}
            onChange={(v) => setData({ email: v })}
            placeholder="you@example.com"
            type="email"
          />
        </Field>
        <Field label="City">
          <TextInput
            testId="input-city"
            value={data.city}
            onChange={(v) => setData({ city: v })}
            placeholder="Bengaluru"
          />
        </Field>
      </div>
      <Field label="Age Group">
        <OptionGrid
          columns={3}
          options={AGE_GROUPS}
          value={data.age_group}
          onChange={(v) => setData({ age_group: v })}
          testIdPrefix="age"
        />
      </Field>
    </div>
  );
}

function StepProfile({ data, setData }) {
  return (
    <div className="space-y-8">
      <Field label="Experience" required>
        <OptionGrid
          columns={3}
          options={EXPERIENCE_OPTIONS}
          value={data.experience}
          onChange={(v) => setData({ experience: v })}
          testIdPrefix="experience"
        />
      </Field>
      <Field label="Markets you trade" hint="Select all that apply.">
        <MultiOptionGrid
          columns={3}
          options={MARKETS}
          value={data.markets}
          onChange={(v) => setData({ markets: v })}
          testIdPrefix="market"
        />
      </Field>
      <Field label="Trading Style" hint="Select all that apply.">
        <MultiOptionGrid
          columns={3}
          options={STYLES}
          value={data.styles}
          onChange={(v) => setData({ styles: v })}
          testIdPrefix="style"
        />
      </Field>
      <Field label="Capital deployed" required>
        <OptionGrid
          columns={3}
          options={CAPITAL_OPTIONS}
          value={data.capital}
          onChange={(v) => setData({ capital: v })}
          testIdPrefix="capital"
        />
      </Field>
      <Field label="Primary Broker" required>
        <div className="relative">
          <select
            value={data.broker}
            onChange={(e) => setData({ broker: e.target.value })}
            data-testid="select-broker"
            className="w-full px-4 py-3 rounded-xl bg-background border border-border text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition appearance-none"
          >
            <option value="">Select your broker</option>
            {BROKERS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
        </div>
      </Field>
    </div>
  );
}

function StepPerformance({ data, setData }) {
  return (
    <div className="space-y-8">
      <Field label="Monthly Result" required>
        <OptionGrid
          columns={3}
          options={MONTHLY_RESULTS}
          value={data.monthly_result}
          onChange={(v) => setData({ monthly_result: v })}
          testIdPrefix="monthly"
        />
      </Field>
      <Field label="Win Rate" required>
        <OptionGrid
          columns={3}
          options={WIN_RATES}
          value={data.win_rate}
          onChange={(v) => setData({ win_rate: v })}
          testIdPrefix="winrate"
        />
      </Field>
      <Field label="Biggest Single Loss" required>
        <OptionGrid
          columns={2}
          options={BIGGEST_LOSSES}
          value={data.biggest_loss}
          onChange={(v) => setData({ biggest_loss: v })}
          testIdPrefix="loss"
        />
      </Field>
      <Field label="Biggest Challenge" hint="Select all that apply.">
        <MultiOptionGrid
          columns={2}
          options={CHALLENGES}
          value={data.challenges}
          onChange={(v) => setData({ challenges: v })}
          testIdPrefix="challenge"
        />
      </Field>
    </div>
  );
}

function StepGoals({ data, setData }) {
  return (
    <div className="space-y-8">
      <Field label="Why do you trade?" required>
        <OptionGrid
          columns={3}
          options={TRADE_REASONS}
          value={data.trade_reason}
          onChange={(v) => setData({ trade_reason: v })}
          testIdPrefix="reason"
        />
      </Field>
      <Field label="Primary Goal" required>
        <OptionGrid
          columns={2}
          options={GOALS}
          value={data.goal}
          onChange={(v) => setData({ goal: v })}
          testIdPrefix="goal"
        />
      </Field>
    </div>
  );
}

/* -------- Step Validation -------- */
function validateStep(step, d) {
  if (step === 0) return d.name.trim().length >= 2 && d.phone.trim().length >= 6;
  if (step === 1) return !!d.experience && !!d.capital && !!d.broker;
  if (step === 2) return !!d.monthly_result && !!d.win_rate && !!d.biggest_loss;
  if (step === 3) return true; // psychology has defaults
  if (step === 4) return !!d.trade_reason && !!d.goal;
  return false;
}
